<template>
    <div class="mt-3">
        <v-row class="px-3">
            <v-col cols="12" align="center" v-if="programs.length === 0">
                <template v-if="loading">
                    <loading-spinner no-margin-top/>
                    <small>{{ $t('common.loading') }}...</small>
                </template>
                <small v-else>{{ $t('programs.no_programs_founded') }}</small>
            </v-col>

            <v-col cols="12" v-for="program in programs" :key="program.objectID" class="pa-4">
                <program-card @click="goToProgram(program.objectID)" :program="program"
                              with-menu @delete="showDeleteProgram(program.objectID)"
                              @assign="goToAssignProgram(program.objectID)" @copy="onCopyProgram(program)"
                              @folder="showSaveFolder(program)"
                />
            </v-col>
            <v-col cols="12" align="right">
                <v-btn
                    @click="loadPrograms"
                    :loading="loading"
                    v-if="hasMore && programs.length > 0"
                    class="normal-btn b-primary"
                >
                    {{ $t('common.load_more') }}
                </v-btn>
            </v-col>
        </v-row>

        <save-in-folder-dialog
            ref="saveInFolder"
            :current-id="programId"
            :default-folder="folderId"
            @cancel="programId = null; folderId = null"
            type="programs"
        />

        <kimsa-confirm
            ref="showDelete"
            :title="$t('programs.delete_title')"
            :content="$t('programs.confirm_deleted')"
            :persistent="false"
            :loading="loadingDelete"
            @confirm="deleteProgram"
            @cancel="programId = null"
            :close-on-confirm="false"
            :alert-mode="true"
        />
    </div>
</template>

<script>
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import {db, Timestamp} from "@/utils/Firebase";
import {debounce} from "@/utils/Common";
import {Client, ProgramIndex, RoutineIndex} from "@/utils/Algolia";
import ProgramCard from "@/components/common/routines/ProgramCard";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import {mapMutations} from "vuex";
import SaveInFolderDialog from "@/components/coach/SaveInFolderDialog";

export default {
    name: "Programs",
    components: {SaveInFolderDialog, LoadingSpinner, ProgramCard, KimsaConfirm, KimsaTextField},
    props: {
        search: {
            type: String,
            default: '',
        },
        folder: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            loading: false,
            loadingDelete: false,
            programId: null,
            programs: [],
            coaches: [],
            folderId: null,
            lastDoc: null,
            hasMore: false,
            perPage: 10,
        }
    },
    async mounted() {
        let vm = this
        await vm.refresh()
    },
    methods: {
        ...mapMutations(['showOverlay', 'hideOverlay']),
        goToProgram(id) {
            const vm = this
            let query = {}
            if (vm.$route.query?.studentId) query.studentId = vm.$route.query.studentId
            vm.$router.push({name: `${vm.accountType}.routines.program.detail`, params: {uid: id}, query: query})
        },
        async showDeleteProgram(id) {
            const vm = this
            vm.programId = id
            vm.kimsaConfirmContent = vm.$t('routines.confirm_deleted_routine')
            vm.$refs.showDelete.show()
        },
        async deleteProgram() {
            const vm = this
            vm.loadingDelete = true
            await db.collection('programs').doc(vm.programId).delete()
            vm.programs = vm.programs.filter(routine => routine.objectID !== vm.programId)
            vm.toast(vm.$t('programs.deleted'))
            vm.programId = null
            vm.$refs.showDelete.hide()
            vm.loadingDelete = false
        },
        debouncedLoadPrograms: debounce(
            function () {
                this.loadPrograms()
            }, 1000),
        async loadPrograms() {
            const vm = this
            vm.loading = true

            let query = db.collection('programs')
                .where('coachId', '==', vm.currentUser.uid)
                .orderBy('name')
                .startAt(vm.search)
                .endAt(vm.search + '\uf8ff')
                .limit(vm.perPage);

            if (vm.folder) query = query.where('folder', '==', vm.folder);

            if (vm.lastDoc) query = query.startAfter(vm.lastDoc);

            const programsRef = await query.get();
            const newPrograms = programsRef.docs.map((program) => ({
                objectID: program.id,
                ...program.data(),
            }));

            vm.hasMore = newPrograms.length === vm.perPage

            vm.programs = [...vm.programs, ...newPrograms];
            vm.lastDoc = programsRef.docs[programsRef.docs.length - 1];
            vm.loading = false;
        },
        goToAssignProgram(id) {
            const vm = this
            let query = {}
            if (vm.$route.query?.studentId) query.studentId = vm.$route.query.studentId
            vm.$router.push({name: `${vm.accountType}.routines.program.assign`, params: {uid: id}, query: query})
        },
        async onCopyProgram(program) {
            const vm = this
            vm.showOverlay(true)
            // console.log('program', program)

            let payload = {
                ...program,
                coachId: vm.currentUser.uid,
                name: `${program.name} ${vm.$t('common.copy_template')}`
            }
            delete payload.created_at
            delete payload.objectID
            payload = JSON.parse(JSON.stringify(payload))
            await db.collection('programs').add({...payload, created_at: Timestamp.now()})

            vm.hideOverlay()
            vm.toast(vm.$t('programs.copied'))
            vm.refresh()
        },
        showSaveFolder(program) {
            const vm = this
            vm.programId = program.objectID
            vm.folderId = program.folder || null
            vm.$refs.saveInFolder.show()
        },
        refresh() {
            const vm = this
            vm.hasMore = false
            vm.programs = []
            vm.lastDoc = null
            vm.loadPrograms()
        }
    },
    watch: {
        search(val) {
            this.programs = []
            if (!val) return this.refresh()
            return this.debouncedLoadPrograms()
        },
        loading(val) {
            this.$emit('loading', val)
        },
        folder() {
            this.programs = []
            this.refresh()
        }
    }
}
</script>

<style scoped>

</style>